import { useEffect, useRef, useState } from 'react';

import { User, useFindOrCreateUserFromEmail, useUserCause } from 'core/Api';
import { CauseLogo, CreateAccountForm, SignInForm } from 'core/Components';
import { useSearchParams } from 'react-router-dom';
import {
  Affix,
  ButtonMantine,
  Card,
  Center,
  Group,
  ImageMantine,
  List,
  LoadingOverlay,
  Modal,
  ScrollArea,
  Stack,
  Text,
  Title,
} from 'ui';

import { useEmailStorage } from './hooks';

type SetTrackingCode = (user: Partial<User>) => Promise<unknown>;

const Experiment_ViewOffersModalHeader = () => {
  const { data: cause } = useUserCause();
  // This really isn't ever going to happen since we don't have the users cause information... Keeping the code incase it does happen
  return (
    <Group wrap="nowrap">
      <CauseLogo cause_uuid={cause?.uuid} size="sm" />
      <Group gap={'xs'} wrap="nowrap">
        <Text fz="lg" c="white" fw={600}>
          Shop today & support
        </Text>
        <Text fz="lg" c="tangerine" fw={600}>
          {cause?.name ?? 'a cause of your choice'}
        </Text>
      </Group>
    </Group>
  );
};

const Experiment_ViewOffersModalFooter = ({
  onSkip,
  signInButtonRef,
}: {
  onComplete: () => void;
  onSkip: () => void;
  signInButtonRef: React.RefObject<SignInButtonProps>;
}) => {
  const { localStorageEmail } = useEmailStorage();

  const handleComplete = async () => {
    const onClick = signInButtonRef.current?.onClick as () => void;
    onClick?.();
  };
  return (
    <Affix withinPortal={false} w="100%" px="lg" py="md" bg="white">
      <Stack gap={0}>
        <ButtonMantine
          variant="gradient"
          onClick={handleComplete}
          loading={signInButtonRef.current?.isLoading}
          disabled={signInButtonRef.current?.isLoading}
        >
          {localStorageEmail ? 'Save Password & View Offers' : 'CreateAccount & View Offers'}
        </ButtonMantine>
        <ButtonMantine variant="subtle" color="gray" onClick={onSkip}>
          Skip for now
        </ButtonMantine>
      </Stack>
    </Affix>
  );
};

type ViewOffersProps = {
  signInButtonRef: React.RefObject<SignInButtonProps>;
  onComplete: () => void;
  setIsSignUp: (isSignUp: boolean) => void;
};

const Experiment_ViewOffersModalContent = ({
  signInButtonRef,
  onComplete,
  setIsSignUp,
}: ViewOffersProps) => {
  const { localStorageEmail } = useEmailStorage();
  return (
    <Stack>
      <Stack>
        <Title order={2}>
          {localStorageEmail
            ? "You've unlocked online offers!"
            : 'One more step to unlock online offers!'}
        </Title>
        <Title order={4}>
          {localStorageEmail
            ? 'Set your password to access your account in the future'
            : 'Finish creating your Givebacks account'}
        </Title>
        <CreateAccountForm
          initialEmail={localStorageEmail}
          isEmailDisabled={Boolean(localStorageEmail)} // We want the email disabled because we've already had the user verify it's their email
          signInButton={(onClick, isLoading) => {
            if (signInButtonRef && signInButtonRef.current) {
              signInButtonRef.current.onClick = onClick;
              signInButtonRef.current.isLoading = isLoading;
            }
            return null;
          }}
          onSuccess={onComplete}
          toggleSignIn={() => setIsSignUp(false)}
        />
      </Stack>
      <Card p="lg" radius={'sm'} bg="cobalt.0" mb={160}>
        <Stack gap={'lg'}>
          <Stack gap={4}>
            <Title order={4}>How to use the Givebacks extension:</Title>
            <List fz="sm" c="gray" type="ordered">
              <List.Item>Shop online at one of our 10,000+ store partners.</List.Item>
              <List.Item>
                Click the "Activate" button on the extension before you check out.
              </List.Item>
              <List.Item>
                Our store partners donate a portion of the purchase to your cause!
              </List.Item>
            </List>
          </Stack>
          <Center mx={48}>
            <ImageMantine src="https://s3.amazonaws.com/com.givebacks.public/images/experiments/images/Extention-HowItWorks.gif" />
          </Center>
        </Stack>
      </Card>
    </Stack>
  );
};

type SignInButtonProps = {
  onClick: () => void;
  isLoading: boolean;
};

const ViewOffersModalContent = ({
  handleClose,
  setTrackingCode,
}: {
  handleClose: () => void;
  setTrackingCode: SetTrackingCode;
}) => {
  const [userInfo, setUserInfo] = useState<Partial<User> | undefined>();
  const [isSignUp, setIsSignUp] = useState(true);
  const signInButtonRef = useRef<SignInButtonProps>({ onClick: () => null, isLoading: false });
  const { mutate: findOrCreateUser, isPending: isFindLoading } = useFindOrCreateUserFromEmail();
  const { localStorageEmail } = useEmailStorage();

  useEffect(() => {
    // We want to attempt to find the user if we have an email in local storage and then set that users uuid to the tracking code
    if (localStorageEmail && !isFindLoading) {
      findOrCreateUser(localStorageEmail, {
        onSuccess(data) {
          setUserInfo(data);
          setTrackingCode(data);
        },
      });
    }
  }, [localStorageEmail]);

  return (
    <>
      {isSignUp ? (
        <>
          <Experiment_ViewOffersModalContent
            signInButtonRef={signInButtonRef}
            onComplete={handleClose}
            setIsSignUp={setIsSignUp}
          />
          <Experiment_ViewOffersModalFooter
            onSkip={() => {
              userInfo && setTrackingCode(userInfo);
              handleClose();
            }}
            onComplete={() => handleClose()}
            signInButtonRef={signInButtonRef}
          />
        </>
      ) : (
        <Stack>
          <Title order={2}>Sign In</Title>
          <SignInForm onSuccess={handleClose} />
          <ButtonMantine variant="subtle" color="gray" onClick={() => setIsSignUp(true)}>
            Create an Account
          </ButtonMantine>
        </Stack>
      )}
      <LoadingOverlay visible={isFindLoading} />
    </>
  );
};

const Experiment_ViewOffersModal = ({ setTrackingCode }: { setTrackingCode: SetTrackingCode }) => {
  const [opened, setOpened] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const isOnboardingExtensionPath = searchParams.get('from') == 'x-onboarding-extension';
  const { removeLocalStorageEmail } = useEmailStorage();

  const handleClose = () => {
    // Before Closing, We need to account for
    // 1. removing the search param from the url
    // 2. removing the email from local storage
    searchParams.delete('from');
    setSearchParams(searchParams);
    removeLocalStorageEmail();
    setOpened(false);
  };

  useEffect(() => {
    // Assuming we are on the onboarding extension path, we want to open the modal...
    isOnboardingExtensionPath && setOpened(isOnboardingExtensionPath);
  }, [isOnboardingExtensionPath]);

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={<Experiment_ViewOffersModalHeader />}
      variant="right-aligned"
      scrollAreaComponent={ScrollArea.Autosize}
      closeOnClickOutside={false}
      withCloseButton={false}
    >
      <ViewOffersModalContent handleClose={handleClose} setTrackingCode={setTrackingCode} />
    </Modal>
  );
};

export { Experiment_ViewOffersModal };
